import { MouseEvent, SVGProps } from "react"
import styled from "styled-components"

import {
  ButtonStyleReset,
  CircularProgress,
  colors,
  colorScale,
  spacing,
} from "@minuthq/meatball-ui-react"

export type GlowingButtonProps = {
  StartIcon?: React.FC<SVGProps<SVGSVGElement>>
  EndIcon?: React.FC<SVGProps<SVGSVGElement>>
  children: React.ReactNode
  onClick: (e: MouseEvent) => void
  loading?: boolean
  disabled?: boolean
}

export function GlowingButton({
  onClick,
  StartIcon,
  EndIcon,
  children,
  loading,
  disabled,
}: GlowingButtonProps) {
  const internalDisabled = disabled || loading

  return (
    <StyledButton onClick={onClick} disabled={internalDisabled}>
      <Box $hidden={loading}>
        {StartIcon && <div>{<StartIcon width={18} height={18} />}</div>}

        <div>{children}</div>

        {EndIcon && <div>{<EndIcon width={18} height={18} />}</div>}
      </Box>

      <LoadingWrapper $hidden={!loading}>
        <CircularProgress size={18} />
      </LoadingWrapper>
    </StyledButton>
  )
}

const StyledButton = styled.button`
  ${ButtonStyleReset}

  --border-width: 1px;
  --blur-width: 1px;
  --blur: 3px;
  --radius: 0.5rem;
  --animation-duration: 3s;
  --animation-easing: linear;
  --animation-state: paused;
  --colors: ${colorScale.blue[200]}, ${colors.primary}, ${colorScale.blue[200]};

  @property --angle {
    syntax: "<angle>";
    initial-value: 0deg;
    inherits: false;
  }

  position: relative;
  color: ${colors.primary};
  font-size: 0.825rem;
  padding: ${spacing.XS} ${spacing.M};
  background-color: ${colors.neutralLight};
  border-radius: var(--radius);
  text-decoration: underline;
  overflow: visible;

  &::before {
    content: "";

    position: absolute;
    inset: calc(var(--border-width) * -1);
    background-image: conic-gradient(from var(--angle), var(--colors));
    opacity: 0;
    animation: glow-spin var(--animation-duration) var(--animation-easing)
      infinite;
    animation-play-state: var(--animation-state);
    border-radius: calc(var(--radius) + var(--border-width));
    z-index: -1;
  }

  &::after {
    content: "";

    position: absolute;
    inset: calc(var(--blur-width) * -1);
    filter: blur(var(--blur));
    background-image: conic-gradient(from var(--angle), var(--colors));
    opacity: 1;
    animation: glow-spin var(--animation-duration) var(--animation-easing)
      infinite;
    animation-play-state: var(--animation-state);
    border-radius: calc(var(--radius) + var(--blur-width));
    z-index: -1;
  }

  &:hover:not(:disabled) {
    --animation-state: running;
  }

  &:hover:not(:disabled)::before {
    opacity: 1;
  }

  @media (prefers-reduced-motion: reduce) {
    --animation-state: paused;
    --animation-duration: 0s;
  }

  @keyframes glow-spin {
    to {
      --angle: 360deg;
      transform: translateZ(0px);
    }
  }

  &:disabled {
    cursor: auto;
    color: ${colorScale.blue[400]};
    --colors:
      ${colorScale.blue[200]}, ${colorScale.blue[300]}, ${colorScale.blue[200]};
  }
`

const Box = styled.div<{ $hidden?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing.XS2};
  opacity: 1;
  transition: opacity 0.2s ease;

  ${({ $hidden }) => $hidden && "opacity: 0;"}
`

const LoadingWrapper = styled.div<{ $hidden?: boolean }>`
  display: flex;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  opacity: 1;

  ${({ $hidden }) => $hidden && "opacity: 0;"}
  transition: opacity 0.2s ease;
`
